import type { JSX } from 'react'
import type { List } from 'immutable'
import { camelCase } from 'lodash-es'
import { useSelector } from 'react-redux'
import Immutable from 'immutable'

import type { CheckoutProps } from './Checkout'
import type { PersonalDataField } from './personalDataByCountry'
import { personalDataFieldsByCountry } from './personalDataByCountry'

const stepsTranslationScope = 'views.storefrontView.checkoutPageSection.steps'

/**
 * Returns a function that gives the translation key of a step.
 *
 * Example usage:
 * ```
 * const getStepTranslationKey = useStepTranslationKey()
 * const shippingKey = getStepTranslationKey('shipping')
 * ```
 */
export function useStepTranslationKey(): (stepId: CheckoutProps['stepId']) => string {
  const cart = useSelector<State, ImmutableMap>((state) => state.get('cart'))
  const shippingCountries = useSelector<State, ImmutableList>((state) => state.getIn(['checkout', 'countries']))

  function getStepTranslationKey(stepId: CheckoutProps['stepId']): string {
    if (stepId !== 'shipping') return `${stepsTranslationScope}.${camelCase(stepId)}.label`

    const showPickupLabel = cart.hasIn(['_links', 'pickup-option-available'])
    const showShippingLabel = shippingCountries.size > 0
    const showShippingAndPickupLabel = showShippingLabel && showPickupLabel

    if (showShippingAndPickupLabel) return `${stepsTranslationScope}.shipping.shippingAndPickup.label`
    if (showShippingLabel) return `${stepsTranslationScope}.shipping.label`
    if (showPickupLabel) return `${stepsTranslationScope}.shipping.pickup.label`

    return ''
  }

  return getStepTranslationKey
}

export function lineListToHTML(list: string[] | List<string>) {
  const line = list.map((line: string, idx: number) => (
    <span key={idx}>
      {line}
      <br />
    </span>
  ))

  // TODO: The explicit typing as `JSX.Element[]` is needed for using `immutableList.map()`
  // inside JSX code with Immutable v3 and React 18. Can be removed when updating to
  // Immutable v4 (https://epages.atlassian.net/browse/EPUI-1426).
  //
  // `list.map()` returns the type `Iterable<number, JSX.Element>` in Immutable v3. This
  // is incompatible when using inside JSX in React 18 because it "is not assignable to
  // type `ReactNode`".
  return line as JSX.Element[]
}

export function methodToString(method: ImmutableMap) {
  if (!method) return <span />
  return lineListToHTML([method.get('name'), method.get('description')].filter(Boolean))
}

export function addressToString(address: ImmutableMap | Record<string, any>) {
  if (!address?.get('displayAddressLines')) return <span />
  return lineListToHTML(address.get('displayAddressLines'))
}

function injectUniqueBillingAddressLines(
  address: ImmutableMap,
  taxNumber: string | null | undefined,
  vatId: string | null | undefined,
  scopedT: (key: string, options?: Record<string, any>) => string,
) {
  const lines = address.get('displayAddressLines').toJS() as string[]
  const taxNumberLine = taxNumber ? scopedT('taxNumber', { taxNumber }) : null
  const vatIdLine = vatId ? scopedT('vatId', { vatId }) : null
  const company = address.get('company')
  const companyIndex = company ? lines.indexOf(company) : -1

  if (companyIndex > -1) {
    if (taxNumberLine) lines.splice(companyIndex + 1, 0, taxNumberLine)
    if (vatIdLine) lines.splice(companyIndex + 1, 0, vatIdLine)
  } else {
    if (taxNumberLine) lines.unshift(taxNumberLine)
    if (vatIdLine) lines.unshift(vatIdLine)
  }

  return lines
}

export function billingAddressToString(
  address: ImmutableMap,
  scopedT: (key: string, options?: Record<string, any>) => string,
) {
  if (!address?.get('displayAddressLines')) return <span />

  const taxNumber = address.get('taxNumber')
  const vatId = address.get('vatId')
  if (!vatId && !taxNumber) return lineListToHTML(address.get('displayAddressLines'))
  return lineListToHTML(injectUniqueBillingAddressLines(address, taxNumber, vatId, scopedT))
}

export function billingAddressTypeToString(
  address: Core.Address,
  scopedT: (key: string, options?: Record<string, any>) => string,
) {
  return billingAddressToString(Immutable.fromJS(address as any), scopedT)
}

export function addressTypeToString(address?: Pick<Core.Address, 'displayAddressLines'>) {
  if (!address?.displayAddressLines) return <span />
  return lineListToHTML(address.displayAddressLines)
}

export function areAddressesTheSame(
  billingAddress?: Core.Address | null,
  shippingAddress?: Core.Address | null,
): boolean {
  if (!billingAddress && !shippingAddress) return true
  if (!billingAddress || !shippingAddress) return false

  const stringifiedBillingAddress = JSON.stringify(billingAddress.displayAddressLines)
  const stringifiedShippingAddress = JSON.stringify(shippingAddress.displayAddressLines)

  return stringifiedBillingAddress === stringifiedShippingAddress
}

export function isAddressValid(address?: Core.Address, mustEnterPhoneNumber?: boolean): boolean {
  if (!address) return false

  let mandatoryFields = personalDataFieldsByCountry(address.country).filter((field) => field.required === true)

  // If phone field is deactivated in the cockpit's checkout settings, we are removing it from the mandatory fields
  if (!mustEnterPhoneNumber) {
    mandatoryFields = mandatoryFields.filter((field) => field.name !== 'phone')
  }

  const isFilled = (field: PersonalDataField) => Boolean(address[field.name])

  return mandatoryFields.every(isFilled)
}
